import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import * as S from "../styles";
import {
  isReferencedCoupon,
  isCapicandiaCoupon,
  isCapicandiaCheckoutCoupon,
  getCapicandiaForVariant,
  getCapicandiaPeriodicity,
  CAPICANDIA_COUPONS,
  CAPICANDIA_DISPLAY,
} from "../validation";
import { trackSignUpAccountCreationFailed } from "app/services/tracking/payments/checkoutStepsEvents";
import DegustacaoMigrationNotice from "../../Cadastro/components/DegustacaoMigrationNotice";
import { getPlanUnitLabel } from "../../Cadastro/plans";
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

const formatBRL = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const parseAmount = amountStr =>
  parseFloat(String(amountStr || "0").replace(/\./g, "").replace(",", "."));

const PlanSummary = ({
  formik,
  plans,
  periodicity,
  setPeriodicity,
  onSubmit,
  disableButton,
  submitting,
  hasCompraGarantida,
  isInfoprodutoresMode = false,
  infoprodutoresCoupon = null,
  isJussubEnabled = false,
}) => {
  const dispatch = useDispatch();
  const coupom = useSelector(state => state.app.coupom);
  const isLoadingCoupom = useSelector(state => state.app.is_loading_coupom);
  // Fonte de verdade da intenção do usuário: formik. Evita stale data do redux
  // (após remover cupom, formik limpa e saga retorna {} sincronizando o estado).
  const formikCoupon = String(formik.values.coupom || "").trim().toUpperCase();
  const activeCouponCode = formikCoupon;
  const hasActiveCoupon = !!formikCoupon;
  const [showCoupon, setShowCoupon] = useState(hasActiveCoupon);
  const [couponInput, setCouponInput] = useState("");
  const [couponFeedback, setCouponFeedback] = useState(null);

  // Cache de validação de cupom: evita re-chamada ao /validate ao trocar cards Capicandia
  const couponCacheRef = useRef({});

  const isCouponValid = hasActiveCoupon && !!coupom?.is_valid;
  const isCapicandiaMode = isCouponValid && isCapicandiaCoupon(activeCouponCode);
  // Cupom Candia (variantes ou especial): bloqueia remoção do cupom no checkout
  const isCapicandiaCheckoutMode =
    isCouponValid && isCapicandiaCheckoutCoupon(activeCouponCode);
  // Modo infoprodutor (spec checkout-jusfy-cupom-trial): painel com copy FIXA
  // do mapa INFOPRODUTORES_COUPONS — o cupom do jusfy-backend existe só para
  // /coupons/validate passar; trial/preço dele não são renderizados (D3).
  const infoprodutor = isInfoprodutoresMode ? infoprodutoresCoupon : null;
  const infoprodutorTrialLabel = infoprodutor
    ? `${infoprodutor.trial_days} dias grátis`
    : "";
  const couponError =
    hasActiveCoupon &&
    !isLoadingCoupom &&
    coupom &&
    coupom.is_valid === false &&
    coupom.invalid_reason;

  useEffect(() => {
    if (hasActiveCoupon) setShowCoupon(true);
  }, [hasActiveCoupon]);

  // Armazena no cache o cupom já validado pelo backend
  useEffect(() => {
    if (!isLoadingCoupom && isCouponValid && activeCouponCode && coupom && Object.keys(coupom).length > 0) {
      couponCacheRef.current[activeCouponCode] = coupom;
    }
  }, [isLoadingCoupom, isCouponValid, activeCouponCode, coupom]);

  // Carrega cupom: usa cache local (sem flicker) ou chama o /validate
  const loadCouponWithCache = code => {
    const cached = couponCacheRef.current[code];
    formik.setFieldValue("coupom", code);
    if (cached) {
      dispatch({ type: "LOAD_COUPOM_SUCCESS", payload: cached });
    } else {
      dispatch({
        type: "LOAD_COUPOM",
        payload: {
          coupom: code,
          isJussubEnabled,
          planId: formik.values.plan?.id,
        },
      });
    }
  };

  // Periodicidade fixada pelo próprio cupom (Capicandia é produto separado, com ciclo
  // próprio — não depende do toggle mensal/anual dos 4 planos, que não existe mais)
  const capicandiaPeriodicity = isCapicandiaMode
    ? (getCapicandiaPeriodicity(activeCouponCode) ?? periodicity)
    : null;

  const capicandiaCardOptions = isCapicandiaMode && capicandiaPeriodicity
    ? Object.entries(CAPICANDIA_DISPLAY)
        .map(([variant, meta]) => ({ variant, ...meta }))
        .sort((a, b) => a.order - b.order)
        .map(({ variant, name }) => {
          const entry = CAPICANDIA_COUPONS[variant]?.[capicandiaPeriodicity];
          if (!entry) return null;
          const amount = parseAmount(entry.amount);
          return {
            variant,
            name,
            code: entry.code,
            amount,
            monthlyEquivalent: capicandiaPeriodicity === "annually" ? amount / 12 : amount,
          };
        })
        .filter(Boolean)
    : [];

  const couponCarriesPricing = isCouponValid && coupom?.amount != null;

  // No Jussub o cupom é um desconto sobre o plano SELECIONADO (o /validity precifica
  // o plano enviado), não um plano fixo como no Jusbill. Então mantemos o grid de
  // planos visível pra trocar de plano — a troca revalida e reprecifica (efeito em
  // index.js). No Jusbill, cupom válido continua colapsando pro card único do cupom.
  const isJussubCouponActive = isJussubEnabled && hasActiveCoupon;
  const showPlanGrid =
    !infoprodutor &&
    !isCapicandiaMode &&
    (isJussubCouponActive || (!isLoadingCoupom && !isCouponValid));
  const currentPlan = couponCarriesPricing ? coupom : formik.values.plan;
  const currentAmount = infoprodutor ? 0 : parseAmount(currentPlan?.amount);
  const oldAmount =
    !infoprodutor && couponCarriesPricing ? parseAmount(coupom?.old_amount) : null;
  const discount =
    oldAmount && oldAmount > currentAmount ? oldAmount - currentAmount : 0;

  const nextChargeAmount =
    !infoprodutor && couponCarriesPricing
      ? parseAmount(coupom?.next_amount || coupom?.old_amount)
      : 0;

  const nextChargeDateLabel = (() => {
    const billingAt = isCouponValid ? coupom?.billing_at : null;
    const ref = billingAt ? new Date(billingAt) : new Date();
    if (!billingAt) {
      if (periodicity === "annually") ref.setFullYear(ref.getFullYear() + 1);
      else ref.setMonth(ref.getMonth() + 1);
    }
    return ref.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  })();

  const planDisplayName = infoprodutor
    ? infoprodutor.product_name
    : isCouponValid
      ? coupom?.description || coupom?.name || formik.values.plan?.name
      : formik.values.plan?.name;

  const planDescription = infoprodutor
    ? `Assinatura de ${infoprodutor.price_label}. Com o cupom ${infoprodutor.code}, os primeiros ${infoprodutor.trial_days} dias são grátis; depois, a cobrança de ${infoprodutor.price_label} é feita automaticamente no cartão cadastrado. Cancele antes e nada é cobrado.`
    : isCouponValid
      ? coupom?.subtitle ||
        coupom?.description ||
        `Oferta válida para contratação do ${planDisplayName}. Cobrança imediata.`
      : "Plataforma jurídica completa com cálculos, consultas e ferramentas para potencializar sua atuação.";

  const selectPlan = id => {
    const nextPlan = plans.find(p => p.id === id);
    if (nextPlan) formik.setFieldValue("plan", nextPlan);
  };

  const hasMonthlyPlans = plans.some(plan => plan.type === "monthly");
  const hasAnnualPlans = plans.some(plan => plan.type === "annually");
  const hasPeriodicityOptions = hasMonthlyPlans && hasAnnualPlans;

  const switchPeriodicity = nextPeriodicity => {
    if (nextPeriodicity === periodicity) return;

    setPeriodicity(nextPeriodicity);

    const currentInternalId = formik.values.plan?.internal_id;
    const nextPlan =
      plans.find(
        plan =>
          plan.type === nextPeriodicity &&
          plan.internal_id === currentInternalId
      ) || plans.find(plan => plan.type === nextPeriodicity);

    if (nextPlan) formik.setFieldValue("plan", nextPlan);
  };

  const selectCapicandia = variant => {
    // Usa a periodicidade fixada pelo cupom, não pelo state de tabs (removidas)
    const effectivePeriodicity = capicandiaPeriodicity || periodicity;
    const code = getCapicandiaForVariant(variant, effectivePeriodicity);
    if (!code || code === activeCouponCode) return;
    loadCouponWithCache(code);
  };

  const visiblePlans = hasPeriodicityOptions
    ? plans.filter(plan => plan.type === periodicity)
    : plans;

  const planOptions = visiblePlans.map(plan => ({
    id: plan.id,
    displayName: plan.name,
    amount: parseAmount(plan.amount),
    unitLabel: getPlanUnitLabel(plan.type),
  }));

  const monthlyReferencePlan = plans.find(plan => plan.type === "monthly");
  const annualReferencePlan = plans.find(plan => plan.type === "annually");

  const applyCoupon = () => {
    const code = (couponInput || "").toUpperCase().trim();
    if (!code) {
      setCouponFeedback({ type: "err", msg: "Digite um cupom." });
      return;
    }
    formik.setFieldValue("coupom", code);
    dispatch({
      type: "LOAD_COUPOM",
      payload: { coupom: code, isJussubEnabled, planId: formik.values.plan?.id },
    });
    setCouponFeedback(null);
  };

  const removeCoupon = () => {
    setCouponInput("");
    formik.setFieldValue("coupom", "");
    formik.setFieldValue("reference_name", "");
    formik.setFieldValue("reference_city", "");
    dispatch({ type: "LOAD_COUPOM", payload: { coupom: "" } });
    setCouponFeedback(null);
  };

  const showCupomReferenceInputs =
    isCouponValid && isReferencedCoupon(activeCouponCode);

  const showCompraGarantida = hasCompraGarantida && !isCouponValid;

  const handleSubmit = () => {
    if (!hasActiveCoupon && couponInput.trim()) {
      trackSignUpAccountCreationFailed({
          error: "WARNING_COUPON_NOT_APPLIED",
          coupon: couponInput,
        });
      setCouponFeedback({
        type: "err",
        msg: "Aplique o cupom digitado ou remova-o para continuar.",
      });
      return;
    }
    if (hasActiveCoupon && couponError) {
      setCouponFeedback({
        type: "err",
        msg: "O cupom aplicado é inválido. Remova-o para continuar.",
      });
      return;
    }
    setCouponFeedback(null);
    onSubmit();
  };

  const errRef = field => formik.errors[field] && formik.touched[field];

  return (
    <S.Summary>
      {!isLoadingCoupom && !isCouponValid && !infoprodutor && hasPeriodicityOptions && (
        <S.PeriodTabs aria-label="Periodicidade do plano">
          <S.PeriodTabWrap>
            <S.PeriodTab
              type="button"
              active={periodicity === "monthly"}
              onClick={() => switchPeriodicity("monthly")}
              aria-pressed={periodicity === "monthly"}
            >
              <span className="tab-label">Mensal</span>
              <span className="tab-price">
                a partir de {formatBRL(parseAmount(monthlyReferencePlan?.amount))}
                <span className="tab-period">/mês</span>
              </span>
              <span className="radio" />
            </S.PeriodTab>
          </S.PeriodTabWrap>

          <S.PeriodTabWrap>
            <S.PeriodTab
              type="button"
              active={periodicity === "annually"}
              onClick={() => switchPeriodicity("annually")}
              aria-pressed={periodicity === "annually"}
            >
              <span className="tab-label">Anual</span>
              <span className="tab-price">
                a partir de {formatBRL(parseAmount(annualReferencePlan?.amount))}
                <span className="tab-period">/ano</span>
              </span>
              <span className="radio" />
            </S.PeriodTab>
          </S.PeriodTabWrap>
        </S.PeriodTabs>
      )}

      <S.PlanInfoCard>
        <div className="top">
          <div>
            <p className="field-label">Seu plano</p>
            <p className="name">{planDisplayName}</p>
          </div>
        </div>
        <p className="desc">{planDescription}</p>

        {infoprodutor && (
          <S.PlanCards count={1}>
            <S.PlanCard as="div" active aria-current="true">
              <span className="plan-radio on" />
              <div className="plan-name">{infoprodutor.product_name}</div>
              <div className="plan-price">
                {infoprodutorTrialLabel}
                <span className="plan-period">
                  {" "}· depois {infoprodutor.price_label}
                </span>
              </div>
            </S.PlanCard>
          </S.PlanCards>
        )}

        {!isLoadingCoupom && isCapicandiaMode && capicandiaCardOptions.length > 0 && (
          <S.PlanCards count={capicandiaCardOptions.length}>
            {capicandiaCardOptions.map(opt => {
              const active = opt.code === activeCouponCode;
              return (
                <S.PlanCard
                  key={opt.variant}
                  type="button"
                  active={active}
                  aria-pressed={active}
                  onClick={() => selectCapicandia(opt.variant)}
                >
                  <span className={`plan-radio${active ? " on" : ""}`} />
                  <div className="plan-name">{opt.name}</div>
                  <div className="plan-price">
                    {formatBRL(opt.monthlyEquivalent)}
                    <span className="plan-period">/mês</span>
                  </div>
                  {capicandiaPeriodicity === "annually" && (
                    <div className="plan-equiv">
                      {formatBRL(opt.amount)} no ano
                    </div>
                  )}
                </S.PlanCard>
              );
            })}
          </S.PlanCards>
        )}

        {showPlanGrid && (
          <>
            <S.PlanCards count={planOptions.length}>
              {planOptions.map(opt => {
                const active = formik.values.plan?.id === opt.id;
                return (
                  <S.PlanCard
                    key={opt.id}
                    type="button"
                    active={active}
                    aria-pressed={active}
                    onClick={() => selectPlan(opt.id)}
                  >
                    <span className={`plan-radio${active ? " on" : ""}`} />
                    <div className="plan-name">{opt.displayName}</div>
                    <div className="plan-price">
                      {formatBRL(opt.amount)}
                      <span className="plan-period">{opt.unitLabel}</span>
                    </div>
                  </S.PlanCard>
                );
              })}
            </S.PlanCards>
            <DegustacaoMigrationNotice plan={formik.values.plan} />
          </>
        )}
      </S.PlanInfoCard>

      <S.PurchaseCard>
        <div className="head">
          <h3>Detalhes da compra</h3>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#838486 !important' }} />
        </div>

        <div className="section-label">
          <span className="bullet" />
          Plano
        </div>

        <div className="line-row">
          <span className="line-name">{planDisplayName}</span>
          <span className={`line-value ${discount > 0 ? "muted strike" : ""}`}>
            {formatBRL(oldAmount && oldAmount > 0 ? oldAmount : currentAmount)}
          </span>
        </div>

        {discount > 0 && (
          <div className="line-row">
            <span className="line-name code">
              {coupom?.code || formik.values.coupom || "DESCONTO"}
            </span>
            <span className="line-value discount">
              - {formatBRL(discount)}
            </span>
          </div>
        )}

        {discount > 0 && (
          <div className="line-row">
            <span className="line-name">Total com desconto</span>
            <span className="line-value bold">{formatBRL(currentAmount)}</span>
          </div>
        )}

        {infoprodutor ? (
          <div className="second-month">
            <div className="left">
              <div className="title">Após {infoprodutor.trial_days} dias</div>
              <div className="date">Cancele antes e nada é cobrado</div>
            </div>
            <div className="right">{infoprodutor.price_label}</div>
          </div>
        ) : periodicity === "annually" ? (
          <div className="second-month">
            <div className="left">
              <div className="title">Em 12x no cartão</div>
              <div className="date">Renova em {nextChargeDateLabel}</div>
            </div>
            <div className="right">{formatBRL((nextChargeAmount > 0 ? nextChargeAmount : currentAmount) / 12)}</div>
          </div>
        ) : (
          isCouponValid &&
          nextChargeAmount > 0 && (
            <div className="second-month">
              <div className="left">
                <div className="title">Após o fim do desconto</div>
              </div>
              <div className="right">Valor cheio</div>
            </div>
          )
        )}

        <S.CouponExpansion>
          <button
            type="button"
            className="head"
            onClick={() => setShowCoupon(s => !s)}
            aria-expanded={showCoupon}
            aria-controls="coupon-expansion-body"
          >
            <span>
              {!hasActiveCoupon
                ? "Aplicar cupom"
                : isLoadingCoupom
                  ? "Validando cupom..."
                  : couponError
                    ? "Cupom inválido"
                    : isCouponValid
                      ? "Cupom aplicado"
                      : "Aplicar cupom"}
            </span>
            <span className={`chev${showCoupon ? " open" : ""}`}>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#5E5E60 !important' }} />
            </span>
          </button>
          {showCoupon && (
            <div id="coupon-expansion-body">
              {hasActiveCoupon ? (
                <div
                  className={`applied${couponError ? " invalid" : ""}${
                    isCouponValid ? " valid" : ""
                  }`}
                >
                  <div className="field">
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 16, color: '#838486 !important' }}
                    />
                    <input
                      type="text"
                      value={activeCouponCode}
                      readOnly
                      aria-label="Cupom aplicado"
                    />
                  </div>
                  {!isCapicandiaCheckoutMode && !infoprodutor && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={removeCoupon}
                    >
                      Remover
                    </button>
                  )}
                </div>
              ) : (
                <div className="body">
                  <input
                    type="text"
                    placeholder="CUPOM"
                    aria-label="Código do cupom"
                    value={couponInput}
                    onChange={e => setCouponInput(e.target.value.toUpperCase())}
                  />
                  <button type="button" onClick={applyCoupon}>
                    Aplicar
                  </button>
                </div>
              )}
              {couponError && (
                <div className="feedback err">Cupom inválido.</div>
              )}
              {couponFeedback && (
                <div className={`feedback ${couponFeedback.type}`}>
                  {couponFeedback.msg}
                </div>
              )}
              {showCupomReferenceInputs && (
                <S.CupomReferenceFields>
                  <div>
                    <label>
                      Nome do responsável <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      name="reference_name"
                      className={`form-control ${errRef("reference_name") ? "is-invalid" : ""}`}
                      value={formik.values.reference_name || ""}
                      onChange={formik.handleChange}
                      onBlur={() => formik.setFieldTouched("reference_name", true)}
                    />
                    {errRef("reference_name") && (
                      <span className="err">{formik.errors.reference_name}</span>
                    )}
                  </div>
                  <div>
                    <label>
                      Nome do município <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      name="reference_city"
                      className={`form-control ${errRef("reference_city") ? "is-invalid" : ""}`}
                      value={formik.values.reference_city || ""}
                      onChange={formik.handleChange}
                      onBlur={() => formik.setFieldTouched("reference_city", true)}
                    />
                    {errRef("reference_city") && (
                      <span className="err">{formik.errors.reference_city}</span>
                    )}
                  </div>
                </S.CupomReferenceFields>
              )}
            </div>
          )}
        </S.CouponExpansion>
      </S.PurchaseCard>

      <S.Total>
        <span>Total a pagar hoje</span>
        <strong>{formatBRL(currentAmount)}</strong>
      </S.Total>

      {showCompraGarantida && (
        <S.CompraGarantida>
          <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
          <span>
            <strong>Compra garantida:</strong> cancele sem burocracias em até
            sete dias com reembolso integral do valor pago.
          </span>
        </S.CompraGarantida>
      )}

      <S.PrimaryButton
        type="button"
        disabled={disableButton || submitting || (infoprodutor && !isCouponValid)}
        onClick={handleSubmit}
        >
        {submitting
          ? "Processando..."
          : infoprodutor
            ? `Começar meus ${infoprodutor.trial_days} dias grátis`
            : "Confirmar e pagar"}
      </S.PrimaryButton>

      {infoprodutor && (
        <S.TermsAcceptance>
          Nada é cobrado hoje. Após {infoprodutor.trial_days} dias,{" "}
          {infoprodutor.price_label}; cancele antes e nada é cobrado.
        </S.TermsAcceptance>
      )}

      <S.TermsAcceptance>
        Ao clicar no botão acima, você concorda com nossos{" "}
        <a
          href="https://jusfy.com.br/client/assets/termos.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Termos de Uso
        </a>
        ,{" "}
        <a
          href="https://jusfy.com.br/client/assets/politica.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Política de Privacidade
        </a>{" "}
        e{" "}
        <a
          href="https://jusfy.com.br/client/assets/politica-de-pagamento.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Política de Pagamento
        </a>
        .
      </S.TermsAcceptance>
    </S.Summary>
  );
};

export default PlanSummary;
